import React, { useContext, useEffect, useState } from 'react'
import { errorToString, InstallerStatus, FUND_RELEASE_TYPES, ChecklistItemType, IChecklistItem } from '@oneethos/shared'
import api from '../api-client'
import { toast } from 'react-toastify'
import { AppContext } from '../appContext'
import actions from '../actions'
import './installer-status-editor.scss'
import { MultiSelect, NumericInput, SingleSelect, TextInput } from './forms'
import { Spinner } from '@fluentui/react'
import { ArrayEditor } from './forms/array-editor'
import { selectChecklistItemsByType } from '../selectors/checklistItems'

type ISCItemEditorProps = {
  checklistItemsDocuments: IChecklistItem[],
  statusConfig: InstallerStatus
  onChange: (sc: InstallerStatus) => void
}

const ISCItemEditor = ({ statusConfig, onChange, checklistItemsDocuments }: ISCItemEditorProps) => {
  return <div className="isc-item-editor">
    <div className="form-group">
      <label>Step Name</label>
      <TextInput
        value={statusConfig.status}
        onChange={status => onChange({ ...statusConfig, status })}
      />
    </div>
    <div className="form-group">
      <label>Required Action</label>
      <TextInput
        value={statusConfig.description}
        onChange={description => onChange({ ...statusConfig, description })}
      />
    </div>
    <div className="form-group">
      <label>Upload Type</label>
      <TextInput
        value={statusConfig.uploadType}
        onChange={uploadType => onChange({ ...statusConfig, uploadType })}
      />
    </div>
    <div className="form-group">
      <label>Allow Contract Changes</label>
      <select className="form-select"
        value={statusConfig.allowContractChanges?.toString() || 'false'}
        onChange={ev => onChange({ ...statusConfig, allowContractChanges: ev.target.value === 'true' })}
      >
        <option value="false">false</option>
        <option value="true">true</option>
      </select>
      <div className="form-text">
        Allow the customer and installer to make changes and re-issue loan docs.
      </div>
    </div>
    <div className="form-group">
      <label>Sequence</label>
      <TextInput
        value={statusConfig.sequence?.toString()}
        emptyOk
        onChange={sequence => onChange({ ...statusConfig, sequence })}
      />
    </div>
    <div className="form-group">
      <label>Owner</label>
      <select className="form-select"
        value={statusConfig.owner}
        onChange={ev => onChange({ ...statusConfig, owner: ev.target.value })}
      >
        <option></option>
        <option value="Consumer">Consumer</option>
        <option value="Bank">Bank</option>
        <option value="Installer">Installer</option>
      </select>
    </div>
    <div className="form-group">
      <label>Represents Project Completion</label>
      <select className="form-select"
        value={statusConfig.isClosed?.toString() || 'false'}
        onChange={ev => onChange({ ...statusConfig, isClosed: ev.target.value === 'true' })}
      >
        <option value="false">false</option>
        <option value="true">true</option>
      </select>
    </div>
    <div className="form-group">
      <label>Deprecated</label>
      <select className="form-select"
        value={statusConfig.deprecated?.toString() || 'false'}
        onChange={ev => onChange({ ...statusConfig, deprecated: ev.target.value === 'true' })}
      >
        <option value="false">false</option>
        <option value="true">true</option>
      </select>
    </div>
    <div className="form-group">
      <label>Progress</label>
      <NumericInput
        value={statusConfig.progress?.toString() || ''}
        onChange={val => onChange({ ...statusConfig, progress: parseInt(val) })}
      />
    </div>
    <div className="form-group">
      <label>Alternate Statuses / Aliases </label>
      <ArrayEditor
        prompt='Add new status alias'
        values={statusConfig.aliases || []}
        itemType='aliases'
        onChange={aliases => onChange({ ...statusConfig, aliases })}
      />
    </div>
    <div className="form-group">
      <label>Checklist Items</label>
      <MultiSelect
        options={checklistItemsDocuments.reduce((acc, { label }) => {
          acc[label] = label
          return acc
        }, {})}
        values={statusConfig.requiredDocuments || []}
        onChange={requiredDocuments => onChange({ ...statusConfig, requiredDocuments })}
      />
    </div>
    <div className="form-group">
      <label>Fund Release Types</label>
      <SingleSelect
        options={FUND_RELEASE_TYPES}
        value={statusConfig.fundReleaseType}
        onChange={fundReleaseType => onChange({ ...statusConfig, fundReleaseType })}
      />
    </div>
  </div>
}

export const InstallerStatusEditor = () => {
  const { dispatch, state: { installersStatusConfig, checklistItemsConfig } } = useContext(AppContext)
  const [list, setList] = useState([])
  const [nav, setNav] = useState('statuses')
  const [saving, setSaving] = useState(false)
  const [activeIdx, setActiveIdx] = useState(null)
  const checklistItems = selectChecklistItemsByType(checklistItemsConfig, ChecklistItemType.DocumentUpload, true)
  useEffect(() => {
    // strip out _ids and the "unknown" status
    const _list = installersStatusConfig.list?.filter(c => c.status !== "unknown").map(item => {
      const sc = { ...item }
      delete sc._id
      return sc
    }) || []

    setList(_list)
  }, [installersStatusConfig.list])

  if (installersStatusConfig.loading) {
    return <div>Loading...</div>
  }

  return (
    <div className="isc-editor">
      <h3>Installer Status Config</h3>
      <div className="alert alert-info">
        Changes to Installer Status Configurations not applied until you
        click "Save Installer Status Config" button at the bottom of this section
      </div>
      <ul className="nav nav-tabs">
        <li className="nav-item">
          <span className={`nav-link ${nav === 'statuses' ? 'active' : ''}`}
            onClick={() => setNav('statuses')}>Statuses</span>
        </li>
      </ul>
      {nav === 'statuses' ? <div className="form-group" style={{ display: 'flex', alignItems: 'flex-start' }}>
        <table className="table isc-statuses table-hover">
          <thead>
            <tr>
              <th>Status</th>
              <th>Owner</th>
              <th>Upload Type</th>
              <th>Seq</th>
              <th>Progress</th>
            </tr>
          </thead>
          <tbody>
            {list.map((sc, idx) => {
              return <tr key={idx}
                className={[
                  activeIdx === idx ? 'active' : '',
                  sc.deprecated ? 'deprecated' : ''
                ].join(' ')}
                onClick={() => setActiveIdx(idx)}>
                <td>
                  <div className="isc-name">{sc.status}</div>
                  <div className="dep-text">
                    {sc.deprecated ? 'deprecated' : null}
                  </div>
                </td>
                <td>{sc.owner}</td>
                <td>{sc.uploadType}</td>
                <td>{sc.sequence}</td>
                <td>{sc.progress}</td>
              </tr>
            })}
          </tbody>
        </table>
        {activeIdx !== null ? <div className="ms-3 isc-item-panel rounded">
          <div className="d-flex justify-content-end">
            <button
              type="button"
              className="btn btn-close"
              onClick={() => setActiveIdx(null)}
              aria-label="Close"
            />
          </div>

          <ISCItemEditor
            checklistItemsDocuments={checklistItems}
            statusConfig={list[activeIdx]}
            onChange={statusConfig => {
              const nextList = [...list]
              nextList.splice(activeIdx, 1, statusConfig)
              setList(nextList)
            }}
          />
        </div> : null
        }
      </div > : null}
      <div className="buttons">
        <button
          type="button"
          className="btn btn-primary"
          disabled={saving}
          onClick={() => {
            setSaving(true)
            const statuses = JSON.parse(JSON.stringify(list))

            api.put('/installers-status', { statuses }).then(res => {
              toast.success('Installer Status Config saved')
              api.get('/installers-status').then(res => {
                dispatch(actions.setInstallersStatusConfig(res))
              }).catch(ex => {
                dispatch(actions.setInstallersStatusConfigError(ex.error))
              }).finally(() => setSaving(false))
            }).catch(ex => toast.error(errorToString(ex)))
              .finally(() => setSaving(false))
          }}
        >{saving ? <Spinner /> : 'Save Installer Status Config'}</button>

        <button className="btn btn-secondary"
          type="button"
          onClick={() => {
            const nextList = [...list]
            nextList.push({ status: '(new)', requiredDocuments: [] })
            setList(nextList)
            setActiveIdx(nextList.length - 1)
          }}
        >Add Status</button>
      </div>
    </div >
  )
}

export default InstallerStatusEditor
